import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { ChevronRight, Search, X } from 'lucide-react'

import { Button, Input } from '@/components'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog'
import { Spinner } from '@/components/ui/spinner'
import { useCompanyStore } from '@/features/company'
import { useDeliverableSetsStore } from '@/features/deliverable-set'
import { useProjectsStore } from '@/features/project'
import FolderOpen from '@/public/images/folder-open.svg'
import { ResponseSearchResultItem } from '@/schema'
import { cn } from '@/utils'
import { MIN_SEARCH_LENGTH, SEARCH_DEBOUNCE_DELAY_MS } from '@/utils/constants'
import {
  buildCompanyRoute,
  buildDeliverableSetRoute,
  buildProjectRoute,
  ROUTES,
} from '@/utils/routes'

import { useDebounce } from '../../../hooks/useDebounce'
import { useGlobalSearch } from '../hook'
import { useGlobalSearchStore } from '../store'

const GLOBAL_SEARCH_LIMIT = 4

const getAvatarInitials = (name: string): string => {
  return (name || '')
    .split(' ')
    .map((word) => word[0])
    .filter((char) => char !== '-')
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const GlobalSearchDialog: React.FC = () => {
  const navigate = useNavigate()

  const { dialogOpen, setDialogOpen } = useGlobalSearchStore()
  const setCompanySearchText = useCompanyStore((s) => s.setSearchText)
  const setProjectSearchText = useProjectsStore((s) => s.setSearchText)
  const setDeliverableSetSearchText = useDeliverableSetsStore(
    (s) => s.setSearchText
  )

  // Debounced search for API calls (only when >= 3 characters)
  const [searchText, setSearchText] = useState<string>('')

  const debouncedSearchText = useDebounce(searchText, SEARCH_DEBOUNCE_DELAY_MS)

  const searchName = useMemo(() => {
    const trimmed = debouncedSearchText?.trim() || ''
    return trimmed.length >= MIN_SEARCH_LENGTH ? trimmed : ''
  }, [debouncedSearchText])

  const {
    data: { companies = [], deliverable_sets = [], projects = [] } = {},
    isLoading,
  } = useGlobalSearch({ search: searchName, rowsPerPage: GLOBAL_SEARCH_LIMIT })

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open)
  }

  const isEmptyResults = useMemo(() => {
    return !companies?.length && !deliverable_sets?.length && !projects?.length
  }, [companies, deliverable_sets, projects])

  const generateGroupItem = (
    group: ResponseSearchResultItem[],
    key: 'companies' | 'projects' | 'deliverable_sets'
  ) => {
    const routeMap = {
      companies: buildCompanyRoute,
      projects: buildProjectRoute,
      deliverable_sets: buildDeliverableSetRoute,
    }
    return (
      <>
        {group.slice(0, 3).map((item) => {
          const route = routeMap[key](item.id)
          return (
            <div key={item.id} className="px-md py-xxs">
              <Link
                to={route}
                className="py-md pr-md pl-2.5 flex items-center gap-2 cursor-pointer no-underline text-primary-900"
                onClick={() => setDialogOpen(false)}
              >
                <div className="rounded-full size-8 bg-brand-solid flex items-center justify-center text-primary-on-brand text-sm font-semibold flex-[0_0_32px]">
                  {getAvatarInitials(item.name)}
                </div>
                <div className="flex-[1_1_auto] truncate">{item.name}</div>
              </Link>
            </div>
          )
        })}
        {group.length > 3 && (
          <div className="px-md py-xxs">
            <button
              type="button"
              className="py-xs pr-md pl-2.5 flex items-center gap-1 text-brand-tertiary-600 text-sm cursor-pointer w-full bg-transparent border-none text-left"
              onClick={() => {
                switch (key) {
                  case 'companies':
                    setCompanySearchText(searchName)
                    navigate(ROUTES.COMPANY)
                    break
                  case 'projects':
                    setProjectSearchText(searchName)
                    navigate(ROUTES.PROJECT)
                    break
                  case 'deliverable_sets':
                    setDeliverableSetSearchText(searchName)
                    navigate(ROUTES.DELIVERABLE_SET)
                    break

                  default:
                    break
                }

                setDialogOpen(false)
              }}
            >
              <span>View all results</span>
              <ChevronRight size={20} />
            </button>
          </div>
        )}
      </>
    )
  }

  return (
    <Dialog open={dialogOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        hideCloseButton
        closeOnInteractOutside={false}
        className={cn(
          'p-0 overflow-hidden bg-white rounded-xl gap-0 translate-y-0',
          'sm:min-w-[640px] sm:max-h-[calc(100dvh-8rem)]',
          'md:top-16'
        )}
      >
        <DialogTitle className="hidden" />
        <DialogDescription className="hidden" />
        <div className="relative w-full flex-[0_0_3.5rem]">
          <Input
            placeholder="Search"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className={cn(
              'w-full shadow-sm py-4 px-4 pl-11 md:text-md text-primary-900 h-14 border-none rounded-none',
              'placeholder:text-placeholder',
              'focus-visible:ring-0',
              searchName && 'pr-11'
            )}
            autoFocus
          />
          <Search
            size={20}
            className="absolute -translate-y-1/2 text-foreground-fg-quaternary-400 left-4 top-1/2"
          />
          {searchName && (
            <X
              size={20}
              className="absolute -translate-y-1/2 cursor-pointer text-foreground-fg-quaternary-400 right-4 top-1/2"
              onClick={() => setSearchText('')}
            />
          )}
        </div>
        {searchName && (
          <div
            className={cn(
              'min-h-[260px] flex flex-[1_1_auto] overflow-y-auto',
              !isLoading && !isEmptyResults
                ? 'p-0 flex-col'
                : 'p-3xl pb-5xl justify-center items-center'
            )}
          >
            {isLoading && <Spinner className="size-8 text-brand-600" />}
            {!isLoading && isEmptyResults ? (
              <div className="flex flex-col items-center gap-3xl">
                <div className="flex flex-col items-center gap-xl">
                  {React.createElement('img', {
                    src: FolderOpen,
                    alt: 'Folder Open',
                  })}
                  <div className="space-y-1 text-center">
                    <span className="font-semibold text-md text-primary-900">
                      No results found
                    </span>
                    <div className="text-sm text-tertiary-600">
                      <p className="m-0">
                        Your search for “{searchName}” returned no matching
                        records.
                      </p>
                      <p className="m-0">Please try different keywords.</p>
                    </div>
                  </div>
                </div>
                <Button
                  variant="outlined"
                  onClick={() => setSearchText('')}
                  className="px-3.5 py-2.5 border-primary text-secondary-700 text-sm font-semibold"
                >
                  Clear search
                </Button>
              </div>
            ) : (
              <>
                {!!companies.length && (
                  <div className="border-t-0 border-b border-solid py-md border-x-0 border-secondary">
                    <div className="px-5 pt-md uppercase text-xs text-tertiary-600 font-medium mb-0.5">
                      Company
                    </div>
                    {generateGroupItem(companies, 'companies')}
                  </div>
                )}
                {!!projects.length && (
                  <div className="border-t-0 border-b border-solid py-md border-x-0 border-secondary">
                    <div className="px-5 pt-md uppercase text-xs text-tertiary-600 font-medium mb-0.5">
                      Project
                    </div>
                    {generateGroupItem(projects, 'projects')}
                  </div>
                )}
                {!!deliverable_sets.length && (
                  <div className="py-md">
                    <div className="px-5 pt-md uppercase text-xs text-tertiary-600 font-medium mb-0.5">
                      Deliverable Set
                    </div>
                    {generateGroupItem(deliverable_sets, 'deliverable_sets')}
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default GlobalSearchDialog
